/**
 * UserAddPopup
 *
 * Prototype dialog to add a user with a role and granular permissions matrix.
 * Currently uses local state and static data; integrates visually with the
 * Manage Permissions UI but does not persist to the API. Save is disabled until
 * at least one permission is selected.
 */
import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  IconButton,
  Box,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Checkbox,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import "./user-add-popup.scss";
import MUIBoxTable from "../../../../common/components/mui-table/mui-box-table";

interface User {
  employeeId: number;
  firstName: string;
  employeeFullName: string;
  lastName: string;
  emailId: string;
  pk: number;
}

interface UserAddPopupProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: "admins" | "elt" | "department_owners";
  users: User[];
  loading: boolean;
  onAddUsers: (employeeIds: number[]) => void;
  selectedUsersData?: any;
}

const UserAddPopup: React.FC<UserAddPopupProps> = ({
  isOpen,
  onClose,
  activeTab,
  users,
  loading,
  onAddUsers,
  selectedUsersData,
}) => {
  const [selectedUsers, setSelectedUsers] = useState<number[]>(
    selectedUsersData?.length
      ? selectedUsersData?.map((user: any) => user?.pk)
      : [],
  );
  const [searchTerm, setSearchTerm] = useState("");

  const filteredUsers = useMemo(() => {
    if (!searchTerm) {
      return users;
    }
    return users.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
  }, [users, searchTerm]);

  const handleUserToggle = useCallback(
    (userId: number) => {
      if (activeTab === "department_owners") {
        setSelectedUsers((prevSelected) =>
          prevSelected.includes(userId)
            ? prevSelected.filter((id) => id !== userId)
            : [userId],
        );
      } else {
        setSelectedUsers((prevSelected) =>
          prevSelected.includes(userId)
            ? prevSelected.filter((id) => id !== userId)
            : [...prevSelected, userId],
        );
      }
    },
    [setSelectedUsers],
  );

  const decoratedUsers = useMemo(
    () =>
      filteredUsers.map((user) => ({
        ...user,
        isSelected: selectedUsers.includes(user.pk),
      })),
    [filteredUsers, selectedUsers],
  );

  const headers = useMemo(
    () => [
      {
        key: "checkbox",
        label: "",
        sortable: false,
        width: "10%",
        render: (user: User & { isSelected: boolean }) => (
          <Checkbox
            checked={user.isSelected}
            onChange={() => handleUserToggle(user.pk)}
          />
        ),
      },
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "email", label: "Email" },
    ],
    [handleUserToggle],
  );

  const handleSave = () => {
    onAddUsers(selectedUsers);
    onClose();
  };

  const noResultText = useMemo(() => {
    if (loading) {
      return "Loading List Of Users...";
    }
    if (searchTerm) {
      return "No users found matching your search";
    }
    return "No users available";
  }, [loading, searchTerm]);

  return (
    <Dialog
      open={isOpen}
      maxWidth="sm"
      fullWidth
      className="add-user-popup"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
    >
      <DialogTitle className="popup-header">
        <Typography>Add User</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 24 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="popup-content" sx={{ overflow: "hidden" }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search User"
          className="search-bar"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        {activeTab === "elt" ? (
          <Box className="user-list-container">
            <List>
              {filteredUsers?.map((user) => {
                return (
                  <ListItem
                    key={user.employeeId}
                    className={
                      selectedUsersData?.find(
                        (userValue: any) => userValue?.pk === user.employeeId,
                      )
                        ? "user-list-item user-list-item-disabeld"
                        : "user-list-item"
                    }
                  >
                    <Checkbox
                      checked={selectedUsers.includes(user.employeeId)}
                      onChange={() => handleUserToggle(user.employeeId)}
                      disabled={
                        selectedUsersData?.length
                          ? selectedUsersData?.find(
                              (userValue: any) =>
                                userValue?.pk === user.employeeId,
                            )
                          : false
                      }
                    />
                    <Box className="user-details">
                      <Box className="user-detail-item" sx={{ width: "30%" }}>
                        <span className="detail-label">First Name</span>
                        <span className="detail-value" title={user.firstName}>
                          {user.firstName}
                        </span>
                      </Box>
                      <Box className="user-detail-item" sx={{ width: "30%" }}>
                        <span className="detail-label">Last Name</span>
                        <span className="detail-value" title={user.lastName}>
                          {user.lastName}
                        </span>
                      </Box>
                      <Box className="user-detail-item" sx={{ width: "40%" }}>
                        <span className="detail-label">Email ID</span>
                        <span className="detail-value" title={user.emailId}>
                          {user.emailId}
                        </span>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ) : (
          <MUIBoxTable
            columns={headers}
            data={decoratedUsers}
            noResultText={noResultText}
          />
        )}
      </DialogContent>
      <DialogActions className="popup-footer">
        <Box>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={onClose}
            className="secondary"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} className="primary">
            Add Users
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserAddPopup;
